// React Query hooks for workspace management

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Workspace,
  WorkspaceVersion, // Used in useWorkspaceVersions return type
  WorkspaceListItem,
  SaveWorkspaceRequest,
  UpdateWorkspaceRequest,
  FrontendWorkspaceRequest,
  LoadWorkspaceOptions,
  ListWorkspacesParams,
} from '@/types/workspace';
import { FailureRecord } from '@/types/failure';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { workspacesApi } from '@/services/api/workspacesApi';
import { useCurrentUserId, useCurrentUserInfo } from '@/hooks/useCurrentUserId';

// Type for React Query cache data
interface WorkspaceListQueryData {
  workspaces: WorkspaceListItem[];
  total: number;
  page: number;
  size: number;
}

// Query keys for React Query caching
export const WORKSPACE_QUERY_KEYS = {
  all: ['workspaces'] as const,
  lists: () => [...WORKSPACE_QUERY_KEYS.all, 'list'] as const,
  list: (params?: ListWorkspacesParams) => [...WORKSPACE_QUERY_KEYS.lists(), params] as const,
  details: () => [...WORKSPACE_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...WORKSPACE_QUERY_KEYS.details(), id] as const,
  versions: (id: string) => [...WORKSPACE_QUERY_KEYS.detail(id), 'versions'] as const,
};

/**
 * Hook for listing workspaces with filtering and pagination
 */
export function useWorkspaces(params?: ListWorkspacesParams) {
  const userId = useCurrentUserId();

  return useQuery({
    queryKey: WORKSPACE_QUERY_KEYS.list(params),
    queryFn: () => workspacesApi.list(params, userId),
    staleTime: 30000, // 30 seconds
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for loading a specific workspace
 */
export function useWorkspace(id: string, options?: LoadWorkspaceOptions, workspaceType?: 'weibull' | 'vbl') {
  const userId = useCurrentUserId();

  return useQuery({
    queryKey: WORKSPACE_QUERY_KEYS.detail(id),
    queryFn: () => workspacesApi.load(id, options, userId, workspaceType),
    enabled: !!id,
    staleTime: 60000, // 1 minute
    cacheTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for workspace version history
 */
export function useWorkspaceVersions(
  id: string,
  options?: { enabled?: boolean }
): ReturnType<typeof useQuery<WorkspaceVersion[]>> {
  const userId = useCurrentUserId();

  return useQuery({
    queryKey: WORKSPACE_QUERY_KEYS.versions(id),
    queryFn: () => workspacesApi.getVersions(id, userId),
    enabled: options?.enabled ?? !!id,
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for saving a new workspace
 */
export function useSaveWorkspace() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();
  const currentUser = useCurrentUserInfo();

  return useMutation({
    mutationFn: async ({
      request,
      currentFailures,
      currentResults,
    }: {
      request: SaveWorkspaceRequest | FrontendWorkspaceRequest; // Accept both formats
      currentFailures?: FailureRecord[] | unknown[]; // Support both FailureRecord and VblRecord
      currentResults?: WeibullAnalysisResult | unknown; // Support both WeibullAnalysisResult and VblAnalysisResult
    }) => {
      // Transform frontend request to backend format
      const backendRequest: SaveWorkspaceRequest = {
        workspace_name: 'workspace_name' in request ? request.workspace_name : (request as FrontendWorkspaceRequest).name,
        description: request.description,
        tags: request.tags,
        owner: userId, // Use the current user's ID as owner
        workspace_type: 'workspace_type' in request ? request.workspace_type : 'weibull', // Default to weibull if not specified
        configuration: request.configuration,
        current_failures: currentFailures ? { failures: currentFailures as FailureRecord[] } : {},
        current_results: (currentResults as WeibullAnalysisResult) || {},
      };
      
      return workspacesApi.save(backendRequest, currentFailures, currentResults, userId);
    },
    onMutate: async ({ request }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });

      // Snapshot the previous value
      const previousWorkspaces = queryClient.getQueryData(WORKSPACE_QUERY_KEYS.lists());

      // Optimistically update with a temporary workspace
      const optimisticWorkspace: Workspace = {
        id: `temp_${Date.now()}`,
        name: 'workspace_name' in request ? request.workspace_name : (request as FrontendWorkspaceRequest).name,
        description: request.description,
        configuration: request.configuration,
        metadata: {
          userId: userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: 1,
          tags: request.tags,
          dataContext: {
            totalAvailableRecords: 0,
            dateRange: { from: '', to: '' },
            sitesIncluded: [],
            componentsAnalyzed: [],
          },
        },
        createdBy: {
          id: currentUser.id,
          name: currentUser.name,
          email: currentUser.email,
        },
      };

      // Update the cache optimistically
      queryClient.setQueryData(
        WORKSPACE_QUERY_KEYS.lists(),
        (old: WorkspaceListQueryData | undefined) => {
          if (!old) return { workspaces: [optimisticWorkspace], total: 1, page: 1, size: 20 };
          return {
            ...old,
            workspaces: [optimisticWorkspace, ...old.workspaces],
            total: old.total + 1,
          };
        }
      );

      return { previousWorkspaces };
    },
    onError: (err, variables, context) => {
      // Revert the optimistic update on error
      if (context?.previousWorkspaces) {
        queryClient.setQueryData(WORKSPACE_QUERY_KEYS.lists(), context.previousWorkspaces);
      }
    },
    onSuccess: (data) => {
      // Invalidate and refetch workspace lists
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });

      // Set the new workspace in the cache
      queryClient.setQueryData(WORKSPACE_QUERY_KEYS.detail(data.id), {
        workspace: data,
        warnings: [],
      });
    },
  });
}

/**
 * Hook for updating an existing workspace
 */
export function useUpdateWorkspace() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();

  return useMutation({
    mutationFn: async ({
      id,
      request,
      currentFailures,
      currentResults,
    }: {
      id: string;
      request: UpdateWorkspaceRequest | FrontendWorkspaceRequest; // Accept both formats
      currentFailures?: FailureRecord[] | unknown[]; // Support both FailureRecord and VblRecord
      currentResults?: WeibullAnalysisResult | unknown; // Support both WeibullAnalysisResult and VblAnalysisResult
    }) => {
      // Transform frontend request to backend format
      const backendRequest: UpdateWorkspaceRequest = {
        workspace_id: id,
        workspace_name: 'workspace_name' in request ? request.workspace_name : (request as FrontendWorkspaceRequest).name,
        description: request.description,
        tags: request.tags,
        workspace_type: 'workspace_type' in request ? request.workspace_type : 'weibull', // Default to weibull if not specified
        configuration: request.configuration,
        current_failures: currentFailures ? { failures: currentFailures as FailureRecord[] } : {},
        current_results: (currentResults as WeibullAnalysisResult) || {},
        updated_by: userId, // Current user as updater
      };
      
      return workspacesApi.update(id, backendRequest, currentFailures, currentResults, userId);
    },
    onMutate: async ({ id, request }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: WORKSPACE_QUERY_KEYS.detail(id) });
      await queryClient.cancelQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });

      // Snapshot the previous values
      const previousWorkspace = queryClient.getQueryData(WORKSPACE_QUERY_KEYS.detail(id));
      const previousWorkspaces = queryClient.getQueryData(WORKSPACE_QUERY_KEYS.lists());

      // Optimistically update the workspace detail
      queryClient.setQueryData(
        WORKSPACE_QUERY_KEYS.detail(id),
        (old: { workspace: Workspace; warnings: string[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            workspace: {
              ...old.workspace,
              name: ('workspace_name' in request ? request.workspace_name : (request as FrontendWorkspaceRequest).name) || old.workspace.name,
              description: request.description || old.workspace.description,
              configuration: request.configuration || old.workspace.configuration,
              metadata: {
                ...old.workspace.metadata,
                updatedAt: new Date().toISOString(),
                version: old.workspace.metadata.version + 1,
                tags: request.tags || old.workspace.metadata.tags,
              },
            },
          };
        }
      );

      // Optimistically update the workspace in lists
      queryClient.setQueryData(
        WORKSPACE_QUERY_KEYS.lists(),
        (old: WorkspaceListQueryData | undefined) => {
          if (!old) return old;
          return {
            ...old,
            workspaces: old.workspaces.map((w: WorkspaceListItem) =>
              w.id === id
                ? {
                    ...w,
                    name: ('workspace_name' in request ? request.workspace_name : (request as FrontendWorkspaceRequest).name) || w.name,
                    description: request.description || w.description,
                    updatedAt: new Date().toISOString(),
                    version: w.version + 1,
                    tags: request.tags || w.tags,
                  }
                : w
            ),
          };
        }
      );

      return { previousWorkspace, previousWorkspaces };
    },
    onError: (err, variables, context) => {
      // Revert the optimistic updates on error
      if (context?.previousWorkspace) {
        queryClient.setQueryData(
          WORKSPACE_QUERY_KEYS.detail(variables.id),
          context.previousWorkspace
        );
      }
      if (context?.previousWorkspaces) {
        queryClient.setQueryData(WORKSPACE_QUERY_KEYS.lists(), context.previousWorkspaces);
      }
    },
    onSuccess: (data, variables) => {
      // Update the workspace in cache with the server response
      queryClient.setQueryData(WORKSPACE_QUERY_KEYS.detail(variables.id), {
        workspace: data,
        warnings: [],
      });

      // Invalidate and refetch workspace lists to ensure consistency
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });
    },
  });
}
/**
 * Hook for deleting a workspace
 */
export function useDeleteWorkspace() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();

  return useMutation({
    mutationFn: (id: string) => workspacesApi.delete(id, userId),
    onMutate: async (id: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });

      // Snapshot the previous value
      const previousWorkspaces = queryClient.getQueryData(WORKSPACE_QUERY_KEYS.lists());

      // Optimistically remove from the cache
      queryClient.setQueryData(
        WORKSPACE_QUERY_KEYS.lists(),
        (old: WorkspaceListQueryData | undefined) => {
          if (!old) return old;
          return {
            ...old,
            workspaces: old.workspaces.filter((w: WorkspaceListItem) => w.id !== id),
            total: old.total - 1,
          };
        }
      );

      return { previousWorkspaces };
    },
    onError: (err, id, context) => {
      // Revert the optimistic update on error
      if (context?.previousWorkspaces) {
        queryClient.setQueryData(WORKSPACE_QUERY_KEYS.lists(), context.previousWorkspaces);
      }
    },
    onSuccess: (data, id) => {
      // Remove the workspace from cache
      queryClient.removeQueries({ queryKey: WORKSPACE_QUERY_KEYS.detail(id) });
      queryClient.removeQueries({ queryKey: WORKSPACE_QUERY_KEYS.versions(id) });

      // Invalidate workspace lists
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });
    },
  });
}

/**
 * Hook for restoring a workspace version
 */
export function useRestoreWorkspaceVersion() {
  const queryClient = useQueryClient();
  const userId = useCurrentUserId();

  return useMutation({
    mutationFn: ({ id, versionNumber }: { id: string; versionNumber: number }) => {
      return workspacesApi.restoreVersion(id, versionNumber, userId);
    },
    onSuccess: (data, variables) => {
      // Update the workspace in cache
      queryClient.setQueryData(WORKSPACE_QUERY_KEYS.detail(variables.id), {
        workspace: data,
        warnings: [`Restored to version ${variables.versionNumber}`],
      });

      // Invalidate workspace lists and versions
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.versions(variables.id) });
    },
  });
}

/**
 * Utility hook to check if a workspace name already exists
 */
export function useWorkspaceNameExists() {
  const { data: workspaces } = useWorkspaces();

  return (name: string, excludeId?: string) => {
    if (!workspaces?.workspaces) return false;

    return workspaces.workspaces.some(
      (w) => w.name.toLowerCase() === name.toLowerCase() && w.id !== excludeId
    );
  };
}

/**
 * Hook to get recent workspaces (sorted by update time)
 */
export function useRecentWorkspaces(limit: number = 5) {
  const { data, ...rest } = useWorkspaces({
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    size: limit,
  });

  return {
    ...rest,
    data: data
      ? {
          ...data,
          workspaces: data.workspaces.slice(0, limit),
        }
      : undefined,
  };
}

/**
 * Hook to invalidate all workspace-related queries
 */
export function useInvalidateWorkspaces() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: WORKSPACE_QUERY_KEYS.all });
  };
}
